import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

function parseEmail(text) {
  if (!text) return { subject: '', body: '' };
  const m = text.match(/^Subject:\s*(.+?)\n\n([\s\S]+)$/);
  if (m) return { subject: m[1].trim(), body: m[2].trim() };
  return { subject: '', body: text };
}

const fmtMoney = n => n ? '$' + Number(n).toLocaleString() : '—';

// Counters go out from the user's own Gmail. When the agent's reply is already on the deal (synced from a
// Gmail thread), it is pre-filled and the counter is sent IN that thread; a pasted reply (phone call,
// text) is logged first and the counter starts a new email.
export default function NegotiationModal({ deal, dealId, threadId: initialThreadId = null, onClose, onSent }) {
  const navigate = useNavigate();
  const [step, setStep]               = useState('paste');   // paste | analyzing | response | sending | sent | error
  const [agentReply, setAgentReply]   = useState('');
  const [mail, setMail]               = useState(null);      // GET /api/gmail/deals/:id
  const [threadId, setThreadId]       = useState(initialThreadId);
  const [remind, setRemind]           = useState('3');
  const [sentResult, setSentResult]   = useState(null);
  const [analysis, setAnalysis]       = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody]     = useState('');
  const [error, setError]             = useState(null);
  const [retryStep, setRetryStep]     = useState('paste');   // a failed send retries from the edited counter, not from scratch

  useEffect(() => {
    fetch(`/api/gmail/deals/${dealId}`).then(r => r.json()).then(d => {
      setMail(d);
      // Default to the named thread, else the most recent one the agent answered in my mailbox.
      const mine = (d.threads || []).filter(t => t.can_reply);
      const pick = mine.find(t => t.thread_id === initialThreadId) || mine.find(t => !t.awaiting_reply) || mine[0];
      if (pick) setThreadId(pick.thread_id);
    }).catch(() => setMail({ connection: { connected: false }, threads: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const myThreads = useMemo(() => (mail?.threads || []).filter(t => t.can_reply), [mail]);
  const thread = myThreads.find(t => t.thread_id === threadId) || null;
  const latestInbound = thread ? [...thread.messages].reverse().find(m => m.direction === 'inbound') : null;

  // Pre-fill the agent's latest synced reply when a thread is chosen and nothing has been typed.
  useEffect(() => {
    if (latestInbound && !agentReply.trim()) setAgentReply(latestInbound.body || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, mail]);

  async function handleAnalyze() {
    if (!agentReply.trim()) return;
    setStep('analyzing');
    try {
      // A reply synced from Gmail is already on the deal; only a pasted one needs logging.
      const alreadyLogged = latestInbound && agentReply.trim() === String(latestInbound.body || '').trim();
      if (!alreadyLogged) {
        const lr = await fetch(`/api/deals/${dealId}/log-agent-response`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: agentReply }),
        });
        if (!lr.ok) throw new Error((await lr.json().catch(() => ({}))).error || 'Could not log the reply');
      }
      // Generate counter
      const r = await fetch(`/api/deals/${dealId}/generate-counter`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_reply: agentReply }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setAnalysis(data);
      const { subject, body } = parseEmail(data.reply_email);
      setReplySubject(subject);
      setReplyBody(body);
      setStep('response');
    } catch (err) {
      setError(err.message);
      setRetryStep('paste');
      setStep('error');
    }
  }

  async function handleSend() {
    setStep('sending');
    try {
      const due = remind ? (() => { const d = new Date(); d.setDate(d.getDate() + Number(remind)); d.setHours(9, 0, 0, 0); return d.toISOString(); })() : null;
      const replyTo = latestInbound ? (String(latestInbound.from).match(/<([^>]+)>/)?.[1] || latestInbound.from) : deal.agent_email;
      const r = await fetch(`/api/deals/${dealId}/send-counter`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: replyTo, subject: replySubject, body: replyBody,
          suggested_offer: analysis.suggested_offer,
          thread_id: threadId || null,
          follow_up: due ? { due_at: due } : null,
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || 'Send failed');
      setSentResult(data);
      setStep('sent');
      if (onSent) onSent(data);
    } catch (err) {
      setError(err.message);
      setRetryStep('response');
      setStep('error');
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 28px 14px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>AI Negotiation Assistant</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
              MAO: <span style={{ color: 'var(--teal)' }}>{fmtMoney(deal.max_offer)}</span> · ARV: <span style={{ color: 'var(--gray-300)' }}>{fmtMoney(deal.ai_arv)}</span> · Round {(deal.negotiation_rounds || 0) + 1}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 28px 24px' }}>
          {step === 'paste' && mail && !mail.connection?.connected && (
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.35)', fontSize: 12, color: 'var(--gray-300)' }}>
              Counters are sent from your Gmail. <span onClick={() => navigate('/settings')} style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }}>Connect Gmail</span> to send one — you can still analyze a reply now.
            </div>
          )}
          {step === 'paste' && (
            <>
              {myThreads.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Conversation</div>
                  <select value={threadId || ''} onChange={e => { setThreadId(e.target.value || null); setAgentReply(''); }} style={inputStyle}>
                    {myThreads.map(t => (
                      <option key={t.thread_id} value={t.thread_id}>
                        {t.subject || '(no subject)'} — {t.awaiting_reply ? 'awaiting reply' : 'agent replied'}
                      </option>
                    ))}
                    <option value="">None — pasted reply, counter as a new email</option>
                  </select>
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {latestInbound && agentReply.trim() === String(latestInbound.body || '').trim() ? "Agent's latest reply (from Gmail)" : "Paste agent's reply"}
              </div>
              <textarea
                value={agentReply}
                onChange={e => setAgentReply(e.target.value)}
                placeholder="Paste in what the listing agent wrote — text, email, or paraphrase. Claude will analyze and suggest a strategic counter."
                rows={10}
                style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, lineHeight: 1.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={onClose} style={btn('transparent', 'var(--gray-500)', 'var(--navy-border)', 1)}>Cancel</button>
                <button onClick={handleAnalyze} disabled={!agentReply.trim()} style={{ ...btn('var(--teal)', '#fff', 'var(--teal)', 2), opacity: agentReply.trim() ? 1 : 0.5 }}>
                  Analyze & Generate Counter
                </button>
              </div>
            </>
          )}

          {step === 'analyzing' && <Loader text="Analyzing reply and generating strategic counter…" />}
          {step === 'sending' && <Loader text="Sending counter…" />}

          {step === 'response' && analysis && (
            <>
              <div style={{ marginBottom: 14, padding: 12, background: 'rgba(107, 103, 96,0.05)', border: '1px solid rgba(107, 103, 96,0.25)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B6760', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Analysis</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <ToneBadge score={analysis.tone_score} />
                  </div>
                </div>
                <p style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.55, margin: 0, marginBottom: 6 }}>{analysis.analysis}</p>
                <p style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600, lineHeight: 1.5, margin: 0 }}>→ {analysis.strategy}</p>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <Stat label="Suggested Offer" value={fmtMoney(analysis.suggested_offer)} color={analysis.suggested_offer && analysis.suggested_offer <= deal.max_offer ? '#4A7C59' : '#B85450'} />
                <Stat label="MAO Cap" value={fmtMoney(deal.max_offer)} color="var(--teal)" />
              </div>

              {analysis.concessions_to_ask && analysis.concessions_to_ask.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Concessions to Request</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {analysis.concessions_to_ask.map((c, i) => (
                      <span key={i} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, background: 'rgba(224, 165, 38,0.1)', color: 'var(--gold)', border: '1px solid rgba(224, 165, 38,0.3)' }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Suggested Reply (Editable)</div>
              {thread
                ? <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>Replying in <b style={{ color: 'var(--gray-300)' }}>{thread.subject}</b></div>
                : <input value={replySubject} onChange={e => setReplySubject(e.target.value)} style={inputStyle} placeholder="Subject" />}
              <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} rows={10} style={{ ...inputStyle, marginTop: 8, lineHeight: 1.55, resize: 'vertical' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--gray-500)' }}>
                Follow-up reminder
                <select value={remind} onChange={e => setRemind(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                  <option value="">None</option>
                  <option value="1">Tomorrow</option>
                  <option value="2">In 2 days</option>
                  <option value="3">In 3 days</option>
                  <option value="7">In a week</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={() => setStep('paste')} style={btn('transparent', 'var(--gray-500)', 'var(--navy-border)', 1)}>Back</button>
                <button onClick={handleSend} disabled={!replyBody.trim() || !mail?.connection?.connected} style={{ ...btn('var(--teal)', '#fff', 'var(--teal)', 2), opacity: replyBody.trim() && mail?.connection?.connected ? 1 : 0.5 }}>
                  {mail?.connection?.connected ? 'Send from Gmail' : 'Connect Gmail to send'}
                </button>
              </div>
            </>
          )}

          {step === 'sent' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(74, 124, 89,0.08)', border: '1px solid rgba(74, 124, 89,0.3)', borderRadius: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4A7C59', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✓</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#4A7C59' }}>{sentResult?.simulated ? `Counter logged, not sent — ${sentResult.reason === 'demo_mode' ? 'demo accounts never send real email' : 'Gmail sending is off'}` : 'Counter sent from your Gmail'}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                    Negotiation round incremented{thread ? ' · in the agent\'s thread' : ''}{sentResult?.followup_id ? ' · follow-up reminder set' : ''}
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{ ...btn('var(--teal)', '#fff', 'var(--teal)', 1), width: '100%' }}>Done</button>
            </div>
          )}

          {step === 'error' && (
            <div>
              <div style={{ padding: '12px 14px', background: 'rgba(184, 84, 80,0.08)', border: '1px solid rgba(184, 84, 80,0.3)', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#B85450' }}>Failed</div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4 }}>{error}</div>
              </div>
              <button onClick={() => setStep(retryStep)} style={{ ...btn('var(--teal)', '#fff', 'var(--teal)', 1), width: '100%' }}>Try Again</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToneBadge({ score }) {
  const color = score >= 70 ? '#4A7C59' : score >= 40 ? '#E0A526' : '#B85450';
  const label = score >= 70 ? 'Promising' : score >= 40 ? 'Lukewarm' : 'Cool';
  return (
    <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 8, background: `${color}15`, color, border: `1px solid ${color}40`, fontWeight: 700 }}>
      Tone {score} · {label}
    </span>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Loader({ text }) {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>{text}</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: '1px solid var(--navy-border)', background: 'var(--navy)',
  color: 'var(--white)', fontSize: 12, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
};

function btn(bg, color, border, flex) {
  return {
    flex, padding: '10px 14px', borderRadius: 8,
    background: bg, border: `1px solid ${border}`,
    color, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  };
}
